'use client';

import { PropsWithChildren, useMemo } from 'react';
import { ConfigProvider, useConfig } from 'apps/cdp-web-demo/app/(main)/ConfigContext';
import { ConfigState } from 'apps/cdp-web-demo/app/types';
import { PortalProvider } from '@cbhq/cds-web/overlays/PortalProvider';
import { MediaQueryProvider, ThemeProvider } from '@cbhq/cds-web/system';
import { defaultTheme } from '@cbhq/cds-web/themes/defaultTheme';

import { getHexFromAccentColor } from './getHexFromAccentColor';

function Content({ children }: PropsWithChildren) {
  const {
    state: { accentColor, themeMode },
  } = useConfig();
  const theme = useMemo(() => {
    return {
      ...defaultTheme,
      darkColor: {
        ...defaultTheme.darkColor,
        bgPrimary: getHexFromAccentColor(accentColor, 'dark'),
        fgPrimary: getHexFromAccentColor(accentColor, 'dark'),
      },
      lightColor: {
        ...defaultTheme.lightColor,
        bgPrimary: getHexFromAccentColor(accentColor, 'light'),
        fgPrimary: getHexFromAccentColor(accentColor, 'light'),
      },
      fontWeight: {
        ...defaultTheme.fontWeight,
        headline: '500',
        label1: '500',
        title3: '500',
      },
    };
  }, [accentColor]);
  return (
    <ThemeProvider theme={theme} activeColorScheme={themeMode}>
      {children}
    </ThemeProvider>
  );
}

type ClientLayoutProps = PropsWithChildren<Partial<Pick<ConfigState, 'themeMode' | 'accentColor'>>>;

export function ClientLayout({ children, accentColor, themeMode }: ClientLayoutProps) {
  const mediaQueryDefaultValues = useMemo(() => {
    return {
      colorScheme: themeMode,
      width: 1200,
    };
  }, [themeMode]);
  const initialConfig = useMemo(() => {
    return {
      themeMode,
      accentColor,
    };
  }, [themeMode, accentColor]);
  return (
    <PortalProvider>
      <ConfigProvider initialConfig={initialConfig}>
        <MediaQueryProvider defaultValues={mediaQueryDefaultValues}>
          <Content>{children}</Content>
        </MediaQueryProvider>
      </ConfigProvider>
    </PortalProvider>
  );
}
